import map from 'lodash/map'
import React from 'react'
import { Badge, Col } from 'react-bootstrap'
import type { OptionProps } from 'react-select'
import { components } from 'react-select'
import type { Chat } from '@ztechapps/common.contract'
import type { DataLoadOptions, FieldEditorInjectedProps, SelectAsyncProps, SelectOption } from '@ztechapps/webui.input'
import { buildConversationLabel, documentLinkKinds } from '@ztechapps/chat.common'
import { LocalDate } from '@ztechapps/webui.date'
import { SelectAsync } from '@ztechapps/webui.input'
import { HorizontalStack } from '@ztechapps/webui.util'
import { ChatStatus } from '../Conversation/ChatStatusBadge'
import { conversationInboxQuery } from '../Inbox/graphql'
import { buildConversationFilter } from './buildConversationFilter'

export type ConversationSelectProps = SelectAsyncProps & Pick<FieldEditorInjectedProps, 'form' | 'meta'>

type ConversationOption = SelectOption & Chat

const documentKindLabel = { orderId: 'Sales order', purchaseOrderId: 'Purchase order', logisticId: 'Logistics' }

const Option = ({ data, ...rest }: OptionProps<ConversationOption>) => {
  const linkKinds = documentLinkKinds(data)

  //always set the isSelected to false so it wont highlight
  return (
    <components.Option {...rest} data={data} isSelected={false}>
      <HorizontalStack gap={2}>
        <div className="flex-shrink-0 text-nowrap">
          <HorizontalStack gap={0}>
            <ChatStatus status={data?.status} />
            <div className="fw-bold">{data?.code}</div>
          </HorizontalStack>
        </div>

        <Col className="text-truncate">
          <small>{data?.subject}</small>
        </Col>

        <Col xs="auto" className="flex-shrink-1 text-truncate text-body-secondary" style={{ maxWidth: '35%' }}>
          <small>{data?.sender?.company || '--'}</small>
        </Col>

        {/* The linked document: its kind from `metadata`, its code from `document` — the same pair the
            details rail's Related document row reads. A conversation carries one document, so its code
            sits on the first kind, as InboxDocumentCell picks it. */}
        {map(linkKinds, (kind, index) => (
          <Badge key={kind} bg="secondary-subtle" text="secondary-emphasis" className="flex-shrink-0 fw-normal">
            {documentKindLabel[kind]}
            {index === 0 && data?.document?.code ? ` ${data.document.code}` : ''}
          </Badge>
        ))}
        <div className="flex-shrink-0 text-nowrap text-body-secondary">
          <small>
            <LocalDate value={data?.lastMessageAt} />
          </small>
        </div>
      </HorizontalStack>
    </components.Option>
  )
}

export const ConversationSelect = ({ filter, form: _form, meta: _meta, ...rest }: ConversationSelectProps) => (
  <SelectAsync
    placeholder="Type conversation code, subject or sender."
    noOptionsMessage={() => 'Cannot find any conversations.'}
    {...rest}
    components={{ Option }}
    dataLoadOptions={
      {
        // `query` is read at runtime by setupLoadOptions but absent from the lib type.
        query: conversationInboxQuery,
        resultFieldName: 'allChat',
        // `input` is the typed search text at runtime; the lib types it loosely as `Record<any, any>`.
        filter: ({ input }) => buildConversationFilter(input as unknown as string, filter),
        // Newest activity first
        options: () => ({ sort: { lastMessageAt: -1 as const } }),
      } as DataLoadOptions
    }
    getOptionLabel={(option) => buildConversationLabel(option?.code, option?.subject, option?.sender?.company)}
  />
)
